'use client';

import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { SUPABASE_ANON_KEY, CONTACT_EMAIL_ENDPOINT } from '@/lib/supabase/config';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll(
      '.reveal, .reveal-left, .reveal-right, .reveal-scale'
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const services = [
  {
    title: 'MIXING',
    desc: 'Full mix from raw tracks. Professional quality balance, spatial placement, dynamics and effects processing.',
  },
  {
    title: 'MASTERING',
    desc: 'Loudness, clarity, and punch. Optimised for streaming platforms, physical release, and live sound. Delivered in WAV and MP3.',
  },
  {
    title: 'STEM MASTERING',
    desc: 'Mastering with limited stem control for additional tonal shaping without a full re-mix.',
  },
  {
    title: 'MIXING + MASTERING BUNDLE',
    desc: 'Full service from raw tracks to release-ready master. Best value option — inquire for bundle pricing.',
  },
];

const processSteps = [
  {
    num: '01',
    title: 'SEND YOUR FILES',
    desc: 'Upload your raw tracks via the file transfer link provided after booking.',
  },
  {
    num: '02',
    title: 'REFERENCE TRACKS',
    desc: 'Provide 2–3 reference tracks so the mix is shaped around your vision.',
  },
  {
    num: '03',
    title: 'FIRST REVISION',
    desc: 'Receive your first mix or master within the agreed turnaround time.',
  },
  {
    num: '04',
    title: 'REVISIONS',
    desc: 'Up to 2 rounds of revisions included. Additional rounds available at extra cost.',
  },
  {
    num: '05',
    title: 'FINAL DELIVERY',
    desc: 'Final files delivered as high-resolution WAV and streaming-ready MP3.',
  },
];

const pricingCards = [
  {
    name: 'MASTERING ONLY',
    price: 'Get a Quote',
    unit: 'per track',
    featured: false,
    inclusions: ['Loudness optimisation', 'EQ & compression', 'Streaming delivery', '1 revision'],
  },
  {
    name: 'FULL MIX + MASTER',
    price: 'Get a Quote',
    unit: 'per song',
    featured: true,
    inclusions: [
      'Full mix from stems',
      'Master included',
      '2 revisions',
      'WAV + MP3 delivery',
      'Reference track matching',
    ],
  },
  {
    name: 'STEM MASTERING',
    price: 'Get a Quote',
    unit: 'per track',
    featured: false,
    inclusions: ['Stem-level tonal control', 'Mastering', 'Streaming delivery', '1 revision'],
  },
];

const testimonials = [
  {
    quote:
      "Having Diggity Glover as our sound and production engineer for the mixing of our records has been nothing but positive. He took the recorded tracks and made us sound as professional and well-produced as our influences. Having him mix us was the best choice we've ever made as a band.",
    name: 'Covert UK',
    band: 'Mixing · Production',
  },
  {
    quote:
      "When it comes to getting your final product exactly how you imagine it, Diggity is the best — he took exactly what I described and turned it into reality with the mixing and mastering. His turnaround time is extremely fast and the results are beyond incredible. I recommend anyone looking for affordable mixing to work with him.",
    name: 'Codex Gigax',
    band: 'Mixing · Mastering · Songwriting',
  },
  {
    quote:
      'Working with Diggity was awesome — we received live mixes of phenomenal quality shortly after our show, which he engineered too. He knows what it takes for modern clarity in a heavy live mix, and adds quality effects like snare bombs and delay smoothly when asked. Epic!',
    name: 'Negative Frame',
    band: 'Live Sound & Multitracks',
  },
];

const faqItems = [
  {
    q: 'What formats do you accept for stems?',
    a: 'WAV or AIFF at 24-bit or 32-bit float, exported at the session sample rate (44.1kHz or 48kHz). Please consolidate all tracks from bar 1 and label them clearly.',
  },
  {
    q: 'How long does a mix or master take?',
    a: 'Standard turnaround is 8–10 business days for a full mix, and 3–5 business days for mastering. Rush options are available at an additional cost.',
  },
  {
    q: 'Do you work with genres outside of Metal?',
    a: "Primarily specialising in Metalcore, Deathcore, and heavy music, but open to adjacent genres. Get in touch and we can discuss whether it's a good fit.",
  },
  {
    q: 'What DAW do you use?',
    a: 'Pro Tools and Logic Pro X are the primary DAWs. Stems are accepted from any DAW — just export consolidated audio files.',
  },
  {
    q: 'How do I send my files?',
    a: 'A file transfer link (WeTransfer or Google Drive) is provided after booking. For large sessions, please zip and compress your stems before uploading.',
  },
  {
    q: 'Can I request more than 2 revisions?',
    a: 'Yes. Additional revision rounds are available at an extra cost per round. This will be outlined in your quote before work begins.',
  },
];

const panelHeader = (title: string) => (
  <div
    style={{
      background: '#ff1a1a',
      color: '#000000',
      fontFamily: 'Manrope, sans-serif',
      fontWeight: 700,
      fontSize: '0.8rem',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      padding: '8px 16px',
    }}
  >
    {title}
  </div>
);

export default function MixingMasteringPage() {
  useScrollReveal();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    bandName: '',
    service: '',
    numTracks: '',
    turnaround: '',
    notes: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    setFormLoading(true);
    setFormError(null);
    try {
      const res = await fetch(CONTACT_EMAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          band: formData.bandName,
          service: `Mixing & Mastering — ${formData.service || 'General enquiry'}`,
          budget: '',
          details: `Service: ${formData.service || '—'}\nNumber of tracks: ${formData.numTracks || '—'}\n\nNotes:\n${formData.notes || '—'}`,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error)
        throw new Error(data.error || 'Something went wrong. Please try again.');
      setFormSubmitted(true);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    background: '#1a1a1a',
    border: '1px solid #ff1a1a',
    color: '#ffffff',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '1rem',
    padding: '10px 14px',
    width: '100%',
    outline: 'none',
    borderRadius: 0,
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '0.7rem',
    letterSpacing: '0.15em',
    textTransform: 'uppercase' as const,
    color: '#cccccc',
    display: 'block',
    marginBottom: '8px',
  };

  return (
    <div
      style={{ minHeight: '100vh', background: '#000000', color: '#ffffff', overflowX: 'hidden' }}
    >
      <Header />

      {/* ─── HERO ─── */}
      <section
        style={{
          padding: '60px 24px 48px',
          borderBottom: '1px solid #ff1a1a',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '380px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: "url('/assets/images/studio-1.jpg')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)' }} />
        </div>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,26,26,0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
          <p
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#ff1a1a',
              marginBottom: '16px',
            }}
          >
            // Services
          </p>
          <h1
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontSize: 'clamp(3rem, 9vw, 7rem)',
              lineHeight: '0.92',
              color: '#ffffff',
              textShadow: '0 0 20px rgba(255,26,26,0.7)',
              marginBottom: '24px',
            }}
          >
            MIX &amp;
            <br />
            <span style={{ color: '#ff1a1a' }}>MASTERING</span>
          </h1>
          <p
            style={{
              fontFamily: 'Manrope, sans-serif',
              color: '#cccccc',
              fontSize: '1.1rem',
              maxWidth: '500px',
              lineHeight: '1.7',
              marginBottom: '32px',
            }}
          >
            Professional mixing and mastering for Metalcore, Deathcore and heavy music.
          </p>
          <a
            href="#book"
            className="btn-primary"
            style={{ fontSize: '0.9rem', padding: '14px 36px' }}
          >
            Get a Quote
          </a>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <section style={{ padding: '60px 24px', borderBottom: '1px solid rgba(255,26,26,0.3)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // What I Offer
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Services
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '12px',
            }}
          >
            {services.map((s, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{
                  background: '#1a1a1a',
                  border: '1px solid #ff1a1a',
                  borderTop: '3px solid #ff1a1a',
                }}
              >
                {panelHeader(s.title)}
                <div style={{ padding: '20px' }}>
                  <p
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.95rem',
                      lineHeight: '1.7',
                    }}
                  >
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROCESS ─── */}
      <section
        style={{
          padding: '60px 24px',
          borderBottom: '1px solid rgba(255,26,26,0.3)',
          background: '#111111',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // How It Works
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              The Process
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {processSteps.map((step, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{
                  display: 'flex',
                  gap: '24px',
                  padding: '20px 0',
                  borderBottom: '1px solid rgba(255,26,26,0.2)',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    color: '#ff1a1a',
                    border: '1px solid #ff1a1a',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background: 'rgba(255,26,26,0.08)',
                  }}
                >
                  {step.num}
                </div>
                <div>
                  <h3
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.9rem',
                      letterSpacing: '0.1em',
                      color: '#ffffff',
                      marginBottom: '8px',
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.9rem',
                      lineHeight: '1.7',
                    }}
                  >
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section style={{ padding: '60px 24px', borderBottom: '1px solid rgba(255,26,26,0.3)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Rates
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Pricing
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '12px',
            }}
          >
            {pricingCards.map((card, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{
                  background: '#1a1a1a',
                  border: card.featured ? '2px solid #ff1a1a' : '1px solid #ff1a1a',
                  boxShadow: card.featured ? '0 0 20px rgba(255,26,26,0.3)' : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {panelHeader(card.name + (card.featured ? ' ★ FEATURED' : ''))}
                <div
                  style={{
                    padding: '24px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 900,
                        fontSize: '2rem',
                        color: '#ff1a1a',
                        textShadow: '0 0 8px rgba(255,26,26,0.5)',
                        display: 'block',
                      }}
                    >
                      {card.price}
                    </span>
                    <span
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.7rem',
                        color: '#666666',
                        letterSpacing: '0.1em',
                      }}
                    >
                      {card.unit}
                    </span>
                  </div>
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                    {card.inclusions.map((inc, j) => (
                      <li
                        key={j}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          fontFamily: 'Manrope, sans-serif',
                          color: '#cccccc',
                          fontSize: '0.9rem',
                        }}
                      >
                        <span style={{ color: '#ff1a1a', flexShrink: 0 }}>✓</span>
                        {inc}
                      </li>
                    ))}
                  </ul>
                  <Link
                    href="/contact"
                    className="btn-primary"
                    style={{ fontSize: '0.8rem', padding: '10px 20px', textAlign: 'center' }}
                  >
                    Book Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section
        style={{
          padding: '60px 24px',
          borderBottom: '1px solid rgba(255,26,26,0.3)',
          background: '#111111',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Testimonials
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              What Bands Say
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '12px',
            }}
          >
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`reveal stagger-${i + 1}`}
                style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}
              >
                {panelHeader('Testimonial')}
                <div style={{ padding: '20px' }}>
                  <p
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      fontSize: '0.95rem',
                      lineHeight: '1.8',
                      marginBottom: '16px',
                    }}
                  >
                    "{t.quote}"
                  </p>
                  <div style={{ borderTop: '1px solid rgba(255,26,26,0.3)', paddingTop: '12px' }}>
                    <p
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 700,
                        color: '#ffffff',
                        fontSize: '0.9rem',
                      }}
                    >
                      {t.name}
                    </p>
                    <p
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.7rem',
                        color: '#ff1a1a',
                        letterSpacing: '0.1em',
                        marginTop: '4px',
                      }}
                    >
                      {t.band}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section style={{ padding: '60px 24px', borderBottom: '1px solid rgba(255,26,26,0.3)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // FAQ
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Common Questions
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {faqItems.map((item, i) => (
              <div
                key={i}
                className="reveal"
                style={{ border: '1px solid #ff1a1a', background: '#1a1a1a' }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    background: openFaq === i ? 'rgba(255,26,26,0.1)' : 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    padding: '14px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.95rem',
                    transition: 'background 0.2s',
                  }}
                >
                  {item.q}
                  <span style={{ color: '#ff1a1a', flexShrink: 0, marginLeft: '16px' }}>
                    {openFaq === i ? '▲' : '▼'}
                  </span>
                </button>
                {openFaq === i && (
                  <div
                    style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(255,26,26,0.3)' }}
                  >
                    <p
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        color: '#cccccc',
                        fontSize: '0.95rem',
                        lineHeight: '1.7',
                        paddingTop: '12px',
                      }}
                    >
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BOOKING FORM ─── */}
      <section id="book" style={{ padding: '60px 24px 80px', background: '#111111' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '40px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Book
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Get a Quote
            </h2>
          </div>
          {formSubmitted ? (
            <div
              className="reveal"
              style={{
                background: '#1a1a1a',
                border: '1px solid #ff1a1a',
                padding: '48px',
                textAlign: 'center',
              }}
            >
              {panelHeader('Enquiry Received')}
              <div style={{ padding: '32px' }}>
                <h3
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 900,
                    fontSize: '1.8rem',
                    color: '#ffffff',
                    textShadow: '0 0 10px rgba(255,26,26,0.5)',
                    marginBottom: '16px',
                  }}
                >
                  Enquiry Received.
                </h3>
                <p
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    color: '#cccccc',
                    fontSize: '1rem',
                    lineHeight: '1.7',
                  }}
                >
                  Expect a reply within 24–48 hours.
                </p>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleFormSubmit}
                noValidate
              className="reveal"
              style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}
            >
              {panelHeader('Mixing & Mastering Enquiry')}
              <div
                style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                  }}
                >
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleFormChange}
                      required
                      placeholder="Your name"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      placeholder="your@email.com"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Band / Project Name</label>
                  <input
                    type="text"
                    name="bandName"
                    value={formData.bandName}
                    onChange={handleFormChange}
                    placeholder="Your band name"
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                  }}
                >
                  <div>
                    <label style={labelStyle}>Service *</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleFormChange}
                      required
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="" disabled>
                        Select...
                      </option>
                      {[
                        'Mixing Only',
                        'Mastering Only',
                        'Mix + Master Bundle',
                        'Stem Mastering',
                      ].map((s) => (
                        <option key={s} value={s} style={{ background: '#1a1a1a' }}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Number of Tracks</label>
                    <input
                      type="text"
                      name="numTracks"
                      value={formData.numTracks}
                      onChange={handleFormChange}
                      placeholder="e.g. 3"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Additional Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={4}
                    placeholder="Genre, references, timeline, anything else..."
                    style={{ ...inputStyle, resize: 'none' }}
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={formLoading}
                    className="btn-primary"
                    style={{
                      fontSize: '0.9rem',
                      padding: '14px 36px',
                      opacity: formLoading ? 0.6 : 1,
                      cursor: formLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {formLoading ? 'Sending...' : 'Submit Enquiry'}
                  </button>
                  {formError && (
                    <p
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.75rem',
                        color: '#ff1a1a',
                        marginTop: '12px',
                      }}
                    >
                      ⚠ {formError}
                    </p>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
